// src/EvenimenteCalendar.js
import * as React from "react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useDataProvider, useNotify, Title } from "react-admin";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { LocationPicker } from "./LocationPicker";
import { ConfirmareOre } from "./ConfirmareOre";
import { useCanManage, isColaboratorSimple, isResponsabil, getColaboratorId, isSuperAdmin } from "./usePermissions";

const extractId = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value.id;
    if (typeof value === "string") {
        const match = value.match(/\/(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
    }
    return value;
};

const extractIds = (collection) => {
    if (!collection) return [];
    return collection.map((item) => {
        if (typeof item === "object") return item.id;
        if (typeof item === "string") {
            const match = item.match(/\/(\d+)$/);
            return match ? parseInt(match[1], 10) : null;
        }
        return item;
    }).filter(Boolean);
};

const toDatetimeLocal = (value) => {
    if (!value) return "";
    const d = new Date(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toDatetimeLocalWithTime = (dateStr, defaultHour = 8) => {
    if (!dateStr) return "";
    const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T${String(defaultHour).padStart(2, '0')}:00:00`);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Durata in ore intre doua valori de datetime-local; null daca intervalul e
// incomplet sau inversat (tipic: 12 AM ales in loc de 12 PM).
const durataOre = (start, end) => {
    if (!start || !end) return null;
    const diffMs = new Date(end) - new Date(start);
    if (!Number.isFinite(diffMs) || diffMs <= 0) return null;
    return Math.round((diffMs / 3600000) * 100) / 100;
};

const formatDataScurta = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const EvenimenteCalendar = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const calendarRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const canManage = useCanManage();
    const colaboratorSimple = isColaboratorSimple();
    const colaboratorId = getColaboratorId();
    const responsabil = isResponsabil() && !canManage;

    const [echipe, setEchipe] = useState([]);
    const [colaboratori, setColaboratori] = useState([]);
    const [evenimente, setEvenimente] = useState([]);
    const [oreEvenimente, setOreEvenimente] = useState([]);
    const [tenanti, setTenanti] = useState([]);
    const [loading, setLoading] = useState(true);

    const evenimenteRef = useRef([]);
    useEffect(() => { evenimenteRef.current = evenimente; }, [evenimente]);

    // --- Stare dialog creare ---
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newTitlu, setNewTitlu] = useState("");
    const [newDescriere, setNewDescriere] = useState("");
    const [newDataStart, setNewDataStart] = useState("");
    const [newDataEnd, setNewDataEnd] = useState("");
    const [newLat, setNewLat] = useState(null);
    const [newLng, setNewLng] = useState(null);
    const [newAdresa, setNewAdresa] = useState("");

    // --- Stare dialog editare ---
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editEveniment, setEditEveniment] = useState(null);
    const [editTitlu, setEditTitlu] = useState("");
    const [editDescriere, setEditDescriere] = useState("");
    const [editDataStart, setEditDataStart] = useState("");
    const [editDataEnd, setEditDataEnd] = useState("");
    const [editEchipeIds, setEditEchipeIds] = useState([]);
    const [editLat, setEditLat] = useState(null);
    const [editLng, setEditLng] = useState(null);
    const [editAdresa, setEditAdresa] = useState("");

    const [asociereDialogOpen, setAsociereDialogOpen] = useState(false);
    const [echipaSelectata, setEchipaSelectata] = useState(null);

    // Dropdown filtru tenant pentru superadmin in panoul de echipe
    const [tenantFiltruId, setTenantFiltruId] = useState("");

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const promises = [
                dataProvider.getList("echipe", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }),
                dataProvider.getList("colaboratori", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }),
                dataProvider.getList("evenimente", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }),
                dataProvider.getList("evenimente-ore", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }),
            ];
            if (isSuperAdmin()) {
                promises.push(dataProvider.getList("tenants", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }));
            }
            const [echipeRes, colabRes, evRes, oreRes, tenantiRes] = await Promise.all(promises);
            setEchipe(echipeRes.data);
            setColaboratori(colabRes.data);
            setEvenimente(evRes.data);
            setOreEvenimente(oreRes.data);
            if (tenantiRes) setTenanti(tenantiRes.data);
        } catch (err) {
            notify("Eroare la incarcarea datelor", { type: "error" });
        } finally {
            setLoading(false);
        }
    }, [dataProvider, notify]);

    useEffect(() => { loadData(); }, [loadData]);

    const echipaProprieId = useMemo(() => {
        if (!colaboratorId) return null;
        const colaboratorCurent = colaboratori.find((c) => c.id === colaboratorId);
        return colaboratorCurent ? extractId(colaboratorCurent.echipa) : null;
    }, [colaboratori, colaboratorId]);

    // Echipele afisate in panoul de drag:
    // - superadmin: filtrate dupa tenantul selectat din dropdown (gol daca nu e selectat nimic)
    // - admin: toate echipele proprii
    // - responsabil: doar propria echipa
    const echipeAsociabile = useMemo(() => {
        if (isSuperAdmin()) {
            if (!tenantFiltruId) return [];
            return echipe.filter((e) => String(extractId(e.tenant)) === String(tenantFiltruId));
        }
        if (canManage) return echipe;
        if (responsabil && echipaProprieId) return echipe.filter((e) => e.id === echipaProprieId);
        return [];
    }, [echipe, canManage, responsabil, echipaProprieId, tenantFiltruId]);

    const poateAsociaEchipe = isSuperAdmin() || canManage || (responsabil && echipaProprieId);

    const evenimenteFiltrate = useMemo(() => {
        if (!colaboratorSimple || !colaboratorId) {
            // Superadmin: filtrare dupa tenant selectat din dropdown
            if (isSuperAdmin() && tenantFiltruId) {
                return evenimente.filter((ev) => {
                    const evTenantId = extractId(ev.tenant);
                    return String(evTenantId) === String(tenantFiltruId);
                });
            }
            return evenimente;
        }
        const colaboratorCurent = colaboratori.find((c) => c.id === colaboratorId);
        const echipaColaboratorId = colaboratorCurent ? extractId(colaboratorCurent.echipa) : null;
        const evenimenteConfirmate = new Set(
            oreEvenimente.filter((o) => extractId(o.colaborator) === colaboratorId).map((o) => extractId(o.eveniment))
        );
        return evenimente.filter((ev) => {
            if (evenimenteConfirmate.has(ev.id)) return true;
            if (echipaColaboratorId) {
                const echipeEv = extractIds(ev.echipe);
                if (echipeEv.includes(echipaColaboratorId)) return true;
            }
            return false;
        });
    }, [evenimente, oreEvenimente, colaboratori, colaboratorId, colaboratorSimple, tenantFiltruId]);

    const asociazaEchipaLaEveniment = useCallback(async (echipaId, echipaNume, evenimentId) => {
        const echipeAsociabileIds = echipeAsociabile.map((e) => e.id);
        if (!echipeAsociabileIds.includes(echipaId)) {
            notify("Nu ai dreptul sa asociezi aceasta echipa", { type: "warning" });
            return;
        }
        const eveniment = evenimenteRef.current.find((e) => String(e.id) === String(evenimentId));
        if (!eveniment) return;
        const echipeExistenteIds = extractIds(eveniment.echipe);
        if (echipeExistenteIds.includes(echipaId)) {
            notify(`${echipaNume} este deja asociata acestui eveniment`, { type: "info" });
            return;
        }
        const noualistaEchipe = [
            ...echipeExistenteIds.map((id) => `/api/echipe/${id}`),
            `/api/echipe/${echipaId}`,
        ];
        try {
            await dataProvider.update("evenimente", { id: evenimentId, data: { echipe: noualistaEchipe }, previousData: {} });
            notify(`Echipa "${echipaNume}" asociata`, { type: "success" });
            await loadData();
        } catch (err) {
            notify("Eroare la asociere: " + (err.message || ""), { type: "error" });
        }
    }, [dataProvider, notify, loadData, echipeAsociabile]);

    const handleEchipaTap = (echipa) => {
        if (!isMobile) return;
        setEchipaSelectata(echipa);
        setAsociereDialogOpen(true);
    };

    const handleAsociereEveniment = async (eveniment) => {
        setAsociereDialogOpen(false);
        if (!echipaSelectata) return;
        await asociazaEchipaLaEveniment(echipaSelectata.id, echipaSelectata.nume, eveniment.id);
        setEchipaSelectata(null);
    };

    const handleEventDidMount = (info) => {
        const el = info.el;
        el.addEventListener("dragenter", (e) => { e.preventDefault(); });
        el.addEventListener("dragover", (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
            el.classList.add("drop-target-highlight");
        });
        el.addEventListener("dragleave", () => { el.classList.remove("drop-target-highlight"); });
        el.addEventListener("drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
            el.classList.remove("drop-target-highlight");
            const echipaId = e.dataTransfer.getData("echipaId");
            const echipaNume = e.dataTransfer.getData("echipaNume");
            if (!echipaId) return;
            asociazaEchipaLaEveniment(parseInt(echipaId, 10), echipaNume, info.event.id);
        });
    };

    const now = new Date();
    const calendarEvents = evenimenteFiltrate.map((ev) => {
        const echipeIdsEv = extractIds(ev.echipe);
        const numeEchipe = (ev.echipe || []).map((e) => (typeof e === "object" ? e.nume : null)).filter(Boolean);
        const aTrecut = ev.dataStart && new Date(ev.dataStart) <= now;
        const areOreConfirmate = oreEvenimente.some((o) => extractId(o.eveniment) === ev.id);
        const faraEchipa = echipeIdsEv.length === 0;
        const neconfirmat = aTrecut && !areOreConfirmate && !faraEchipa;

        let backgroundColor, borderColor, textColor;
        if (faraEchipa) {
            backgroundColor = "#fff3e0"; borderColor = "#ef6c00"; textColor = "#e65100";
        } else if (neconfirmat) {
            backgroundColor = "#d32f2f"; borderColor = "#b71c1c"; textColor = "#ffffff";
        }

        return {
            id: String(ev.id),
            title: (faraEchipa ? "⚠ " : "") + ev.titlu + (numeEchipe.length ? `  [${numeEchipe.join(", ")}]` : ""),
            start: ev.dataStart,
            end: ev.dataEnd,
            backgroundColor,
            borderColor,
            textColor,
            color: borderColor,
            classNames: faraEchipa ? ["eveniment-fara-echipa"] : neconfirmat ? ["eveniment-neconfirmat"] : undefined,
        };
    });

    const handleDateSelect = (selectInfo) => {
        if (!canManage) return;
        setNewTitlu("");
        setNewDescriere("");
        setNewDataStart(toDatetimeLocalWithTime(selectInfo.startStr, 8));
        setNewDataEnd(toDatetimeLocalWithTime(selectInfo.endStr, 16));
        setNewLat(null); setNewLng(null); setNewAdresa("");
        setCreateDialogOpen(true);
    };

    const handleDateClick = (dateClickInfo) => {
        if (!canManage) return;
        setNewTitlu("");
        setNewDescriere("");
        setNewDataStart(toDatetimeLocalWithTime(dateClickInfo.dateStr, 8));
        setNewDataEnd(toDatetimeLocalWithTime(dateClickInfo.dateStr, 16));
        setNewLat(null); setNewLng(null); setNewAdresa("");
        setCreateDialogOpen(true);
    };

    const handleCreateEvent = async () => {
        if (!newTitlu.trim()) { notify("Introdu un titlu", { type: "warning" }); return; }
        if (!newDataStart || !newDataEnd) { notify("Introdu data si ora evenimentului", { type: "warning" }); return; }
        if (durataOre(newDataStart, newDataEnd) === null) { notify("Sfarsitul trebuie sa fie dupa inceput (atentie la AM/PM)", { type: "warning" }); return; }
        try {
            await dataProvider.create("evenimente", {
                data: {
                    titlu: newTitlu,
                    descriere: newDescriere.trim() || null,
                    dataStart: new Date(newDataStart).toISOString(),
                    dataEnd: new Date(newDataEnd).toISOString(),
                    echipe: [],
                    latitudine: newLat, longitudine: newLng, adresa: newAdresa || null,
                },
            });
            notify("Eveniment creat", { type: "success" });
            setCreateDialogOpen(false);
            await loadData();
        } catch (err) {
            notify("Eroare la creare: " + (err.message || ""), { type: "error" });
        }
    };

    // Mutare / redimensionare eveniment direct din calendar (drag & drop).
    // FullCalendar muta evenimentul doar in store-ul lui intern; fara handlerul
    // asta modificarea nu ajunge in API si dispare la refresh.
    const persistaIntervalEveniment = async (info, pastreazaDurata) => {
        const ev = evenimente.find((e) => String(e.id) === String(info.event.id));
        if (!ev) { info.revert(); return; }

        const newStart = info.event.start;
        let newEnd = info.event.end;
        if (pastreazaDurata) {
            const durataMs = ev.dataStart && ev.dataEnd
                ? new Date(ev.dataEnd).getTime() - new Date(ev.dataStart).getTime()
                : 0;
            if (durataMs > 0) newEnd = new Date(newStart.getTime() + durataMs);
        }

        if (!newStart || !newEnd || newEnd <= newStart) {
            info.revert();
            notify("Interval invalid dupa mutare", { type: "warning" });
            return;
        }

        // Orele confirmate raman atasate evenimentului, deci se muta odata cu el.
        // Cerem confirmare explicita ca sa nu se schimbe retroactiv ziua unui pontaj
        // dintr-o miscare accidentala de mouse.
        const oreConfirmate = oreEvenimente.filter((o) => extractId(o.eveniment) === ev.id);
        if (oreConfirmate.length > 0) {
            const nrColaboratori = new Set(oreConfirmate.map((o) => extractId(o.colaborator))).size;
            const acceptat = window.confirm(
                `Evenimentul "${ev.titlu}" are ore confirmate pentru ${nrColaboratori} ` +
                `${nrColaboratori === 1 ? "colaborator" : "colaboratori"} ` +
                `(${oreConfirmate.length} ${oreConfirmate.length === 1 ? "inregistrare" : "inregistrari"}).\n\n` +
                `Mutarea din ${formatDataScurta(ev.dataStart)} in ${formatDataScurta(newStart.toISOString())} ` +
                `schimba si ziua in care apar orele deja pontate.\n\nContinui?`
            );
            if (!acceptat) { info.revert(); return; }
        }

        try {
            await dataProvider.update("evenimente", {
                id: ev.id,
                data: { dataStart: newStart.toISOString(), dataEnd: newEnd.toISOString() },
                previousData: {},
            });
            notify("Eveniment mutat", { type: "success" });
            await loadData();
        } catch (err) {
            info.revert();
            notify("Eroare la mutare: " + (err.message || ""), { type: "error" });
        }
    };

    const handleEventDrop = (info) => persistaIntervalEveniment(info, true);
    const handleEventResize = (info) => persistaIntervalEveniment(info, false);

    const handleEventClick = (clickInfo) => {
        const ev = evenimenteRef.current.find((e) => String(e.id) === String(clickInfo.event.id));
        if (!ev) return;
        setEditEveniment(ev);
        setEditDialogOpen(true);
    };

    useEffect(() => {
        if (!editEveniment) return;
        setEditTitlu(editEveniment.titlu || "");
        setEditDescriere(editEveniment.descriere || "");
        setEditDataStart(toDatetimeLocal(editEveniment.dataStart));
        setEditDataEnd(toDatetimeLocal(editEveniment.dataEnd));
        setEditEchipeIds(extractIds(editEveniment.echipe));
        setEditLat(editEveniment.latitudine ?? null);
        setEditLng(editEveniment.longitudine ?? null);
        setEditAdresa(editEveniment.adresa || "");
    }, [editEveniment]);

    const handleRemoveEchipaFromEdit = (echipaId) => {
        setEditEchipeIds((prev) => prev.filter((id) => id !== echipaId));
    };

    const handleSaveEdit = async () => {
        if (!editTitlu.trim()) { notify("Introdu un titlu", { type: "warning" }); return; }
        if (durataOre(editDataStart, editDataEnd) === null) { notify("Sfarsitul trebuie sa fie dupa inceput (atentie la AM/PM)", { type: "warning" }); return; }
        try {
            const echipeInitialeIds = extractIds(editEveniment.echipe);
            const echipeEliminate = echipeInitialeIds.filter((id) => !editEchipeIds.includes(id));
            if (echipeEliminate.length > 0) {
                const colaboratoriEliminati = colaboratori
                    .filter((c) => echipeEliminate.includes(extractId(c.echipa)))
                    .map((c) => c.id);
                const oreDesters = oreEvenimente.filter(
                    (o) => extractId(o.eveniment) === editEveniment.id &&
                        colaboratoriEliminati.includes(extractId(o.colaborator))
                );
                await Promise.all(oreDesters.map((o) => dataProvider.delete("evenimente-ore", { id: o.id })));
                if (oreDesters.length > 0) notify(`Au fost sterse ${oreDesters.length} inregistrari de ore`, { type: "info" });
            }
            await dataProvider.update("evenimente", {
                id: editEveniment.id,
                data: {
                    titlu: editTitlu, descriere: editDescriere,
                    dataStart: new Date(editDataStart).toISOString(),
                    dataEnd: new Date(editDataEnd).toISOString(),
                    echipe: editEchipeIds.map((id) => `/api/echipe/${id}`),
                    latitudine: editLat, longitudine: editLng, adresa: editAdresa || null,
                },
                previousData: {},
            });
            notify("Eveniment actualizat", { type: "success" });
            setEditDialogOpen(false);
            await loadData();
        } catch (err) {
            notify("Eroare la actualizare: " + (err.message || ""), { type: "error" });
        }
    };

    const handleDeleteEvent = async () => {
        try {
            await dataProvider.delete("evenimente", { id: editEveniment.id });
            notify("Eveniment sters", { type: "success" });
            setEditDialogOpen(false);
            await loadData();
        } catch (err) {
            notify("Eroare la stergere: " + (err.message || ""), { type: "error" });
        }
    };

    // Prop stabil pentru <ConfirmareOre>: recreat doar cand se schimba efectiv
    // evenimentul, datele sau echipele - nu la fiecare render al calendarului.
    const evenimentPentruConfirmare = useMemo(() => (editEveniment ? {
        id: editEveniment.id,
        dataStart: editDataStart ? new Date(editDataStart).toISOString() : editEveniment.dataStart,
        dataEnd: editDataEnd ? new Date(editDataEnd).toISOString() : editEveniment.dataEnd,
        echipe: editEchipeIds.map((id) => `/api/echipe/${id}`),
    } : null), [editEveniment, editDataStart, editDataEnd, editEchipeIds]);

    if (loading) return <Card style={{ margin: "1em" }}><CardContent>Se incarca...</CardContent></Card>;

    const echipeMap = Object.fromEntries(echipe.map((e) => [e.id, e.nume]));
    const confirmareKey = editEveniment ? `${editEveniment.id}-${editEchipeIds.join(",")}` : null;
    const evenimenteSortate = [...evenimenteFiltrate].sort((a, b) => new Date(a.dataStart) - new Date(b.dataStart));

    const EchipePanel = poateAsociaEchipe ? (
        <Box sx={{ width: isMobile ? "100%" : 220, flexShrink: 0, border: "1px solid #ddd", borderRadius: 1, p: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Echipe
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {isMobile ? "tap pentru asociere" : "trage pe eveniment"}
                </Typography>
            </Typography>

            {/* Dropdown filtru tenant - doar pentru superadmin */}
            {isSuperAdmin() && (
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Tenant</InputLabel>
                    <Select
                        value={tenantFiltruId}
                        label="Tenant"
                        onChange={(e) => setTenantFiltruId(e.target.value)}
                    >
                        <MenuItem value=""><em>— selecteaza —</em></MenuItem>
                        {tenanti.map((t) => (
                            <MenuItem key={t.id} value={String(t.id)}>{t.slug}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            <Box sx={{ display: "flex", flexDirection: isMobile ? "row" : "column", flexWrap: isMobile ? "wrap" : "nowrap", gap: 1 }}>
                {isSuperAdmin() && !tenantFiltruId ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        Selecteaza un tenant pentru a vedea echipele
                    </Typography>
                ) : (
                    echipeAsociabile.map((e) => (
                        <Box key={e.id}
                            draggable={!isMobile}
                            onDragStart={!isMobile ? (ev) => {
                                ev.dataTransfer.setData("echipaId", String(e.id));
                                ev.dataTransfer.setData("echipaNume", e.nume);
                                ev.dataTransfer.effectAllowed = "copy";
                            } : undefined}
                            onClick={isMobile ? () => handleEchipaTap(e) : undefined}
                            sx={{ cursor: isMobile ? "pointer" : "grab" }}
                        >
                            <Chip label={e.nume} color="primary" variant="outlined" size={isMobile ? "small" : "medium"}
                                clickable={isMobile} sx={{ pointerEvents: isMobile ? "auto" : "none" }} />
                        </Box>
                    ))
                )}
            </Box>
            {!isMobile && (
                <Box sx={{ mt: 2, pt: 1, borderTop: "1px solid #eee" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Legenda:</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: "#fff3e0", border: "1px dashed #ef6c00", flexShrink: 0 }} />
                        <Typography variant="caption">Fara echipa asociata</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: "#d32f2f", flexShrink: 0 }} />
                        <Typography variant="caption">Ore neconfirmate</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: "#1976d2", flexShrink: 0 }} />
                        <Typography variant="caption">Ore confirmate / viitor</Typography>
                    </Box>
                    {responsabil && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
                            Poti asocia doar propria echipa
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    ) : null;

    return (
        <Card style={{ margin: "1em" }}>
            <Title title="Calendar evenimente" />
            <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 2 }}>
                    {EchipePanel}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <style>{`
                            .drop-target-highlight { outline: 2px dashed #1976d2 !important; outline-offset: 2px; }
                            .fc-event { cursor: pointer; }
                            .eveniment-fara-echipa { border-style: dashed !important; }
                            .eveniment-fara-echipa.fc-daygrid-dot-event .fc-daygrid-event-dot,
                            .eveniment-fara-echipa.fc-list-event .fc-list-event-dot {
                                border-color: #ef6c00 !important;
                            }
                            .eveniment-neconfirmat.fc-daygrid-dot-event .fc-daygrid-event-dot,
                            .eveniment-neconfirmat.fc-list-event .fc-list-event-dot {
                                border-color: #d32f2f !important;
                            }
                            @media (max-width: 600px) {
                                .fc-toolbar { flex-wrap: wrap; gap: 4px; }
                                .fc-toolbar-title { font-size: 1rem !important; }
                                .fc-button { padding: 4px 8px !important; font-size: 12px !important; }
                            }
                        `}</style>
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            selectable={canManage}
                            editable={canManage}
                            select={handleDateSelect}
                            dateClick={handleDateClick}
                            eventClick={handleEventClick}
                            eventDrop={handleEventDrop}
                            eventResize={handleEventResize}
                            eventDidMount={handleEventDidMount}
                            events={calendarEvents}
                            height="auto"
                            headerToolbar={isMobile
                                ? { left: "prev,next", center: "title", right: "today" }
                                : { left: "prev,next today", center: "title", right: "dayGridMonth,timeGridWeek" }
                            }
                        />
                    </Box>
                </Box>
            </CardContent>

            {/* Dialog asociere touch */}
            <Dialog open={asociereDialogOpen} onClose={() => { setAsociereDialogOpen(false); setEchipaSelectata(null); }} fullWidth maxWidth="xs">
                <DialogTitle>Asociaza "{echipaSelectata?.nume}" la eveniment</DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {evenimenteSortate.length === 0 ? (
                        <Typography sx={{ p: 2 }} color="text.secondary">Niciun eveniment disponibil</Typography>
                    ) : (
                        <List>
                            {evenimenteSortate.map((ev) => {
                                const echipeEv = extractIds(ev.echipe);
                                const deja = echipaSelectata && echipeEv.includes(echipaSelectata.id);
                                return (
                                    <ListItem key={ev.id} disablePadding>
                                        <ListItemButton onClick={() => !deja && handleAsociereEveniment(ev)} disabled={deja}>
                                            <ListItemText primary={ev.titlu} secondary={formatDataScurta(ev.dataStart)} />
                                            {deja && <Typography variant="caption" color="success.main">asociata</Typography>}
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAsociereDialogOpen(false); setEchipaSelectata(null); }}>Anuleaza</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog creare */}
            {canManage && (
                <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="sm"
                    PaperProps={{ sx: { mx: { xs: 1, sm: 3 }, width: "100%" } }}>
                    <DialogTitle>Eveniment nou</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField autoFocus fullWidth label="Titlu" value={newTitlu} onChange={(e) => setNewTitlu(e.target.value)} />
                            <TextField fullWidth multiline minRows={2} label="Descriere" value={newDescriere} onChange={(e) => setNewDescriere(e.target.value)} />
                            <TextField fullWidth type="datetime-local" label="Inceput" InputLabelProps={{ shrink: true }} value={newDataStart} onChange={(e) => setNewDataStart(e.target.value)} />
                            <TextField fullWidth type="datetime-local" label="Sfarsit" InputLabelProps={{ shrink: true }} value={newDataEnd} onChange={(e) => setNewDataEnd(e.target.value)}
                                error={!!newDataStart && !!newDataEnd && durataOre(newDataStart, newDataEnd) === null}
                                helperText={newDataStart && newDataEnd
                                    ? (durataOre(newDataStart, newDataEnd) === null
                                        ? "Sfarsitul e inaintea inceputului - ai ales AM in loc de PM?"
                                        : `Durata: ${durataOre(newDataStart, newDataEnd)}h`)
                                    : undefined} />
                            <LocationPicker
                                latitudine={newLat} longitudine={newLng} adresa={newAdresa}
                                onChange={(lat, lng, adresaText) => { setNewLat(lat); setNewLng(lng); if (adresaText !== undefined) setNewAdresa(adresaText); }}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateDialogOpen(false)}>Anuleaza</Button>
                        <Button variant="contained" onClick={handleCreateEvent}>Creeaza</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Dialog editare */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm"
                PaperProps={{ sx: { mx: { xs: 1, sm: 3 }, width: "100%", maxWidth: "100%", overflowX: "hidden" } }}>
                <DialogTitle>
                    {canManage ? "Editeaza eveniment" : "Eveniment"}
                    {isSuperAdmin() && (() => {
                        const tenantIri = editEveniment?.tenant;
                        if (!tenantIri) return <span style={{ fontSize: "0.75em", color: "#ef6c00", marginLeft: 8 }}>(fara tenant)</span>;
                        const tenantId = String(tenantIri).match(/\/(\d+)$/)?.[1];
                        const tenant = tenanti.find(t => String(t.id) === String(tenantId));
                        return (
                            <span style={{ fontSize: "0.75em", color: "#ef6c00", marginLeft: 8 }}>
                                ({tenant ? tenant.slug : `tenant#${tenantId}`})
                            </span>
                        );
                    })()}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField fullWidth label="Titlu" value={editTitlu} onChange={(e) => setEditTitlu(e.target.value)} disabled={!canManage} />
                        <TextField fullWidth multiline minRows={2} label="Descriere" value={editDescriere} onChange={(e) => setEditDescriere(e.target.value)} disabled={!canManage} />
                        <TextField fullWidth type="datetime-local" label="Inceput" InputLabelProps={{ shrink: true }} value={editDataStart} onChange={(e) => setEditDataStart(e.target.value)} disabled={!canManage} />
                        <TextField fullWidth type="datetime-local" label="Sfarsit" InputLabelProps={{ shrink: true }} value={editDataEnd} onChange={(e) => setEditDataEnd(e.target.value)} disabled={!canManage}
                            error={!!editDataStart && !!editDataEnd && durataOre(editDataStart, editDataEnd) === null}
                            helperText={editDataStart && editDataEnd
                                ? (durataOre(editDataStart, editDataEnd) === null
                                    ? "Sfarsitul e inaintea inceputului - ai ales AM in loc de PM?"
                                    : `Durata: ${durataOre(editDataStart, editDataEnd)}h`)
                                : undefined} />
                        {canManage && (
                            <LocationPicker
                                latitudine={editLat} longitudine={editLng} adresa={editAdresa}
                                onChange={(lat, lng, adresaText) => { setEditLat(lat); setEditLng(lng); if (adresaText !== undefined) setEditAdresa(adresaText); }}
                                height={isMobile ? 180 : 280}
                            />
                        )}
                        {canManage && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Echipe asociate</Typography>
                                {editEchipeIds.length === 0 && (
                                    <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                                        ⚠ Nicio echipa asociata - evenimentul nu va fi vizibil pentru nicio echipa
                                    </Typography>
                                )}
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {editEchipeIds.map((id) => (
                                        <Chip key={id} label={echipeMap[id] || `Echipa #${id}`} onDelete={() => handleRemoveEchipaFromEdit(id)} color="primary" />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                        <Divider />
                        {evenimentPentruConfirmare && (
                            <ConfirmareOre key={confirmareKey} eveniment={evenimentPentruConfirmare} />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{
                    justifyContent: "space-between",
                    px: { xs: 1, sm: 3 },
                    flexWrap: "wrap",
                    gap: 1,
                }}>
                    <Box>{canManage && <Button color="error" onClick={handleDeleteEvent}>Sterge eveniment</Button>}</Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        {editLat && editLng && (
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                href={`https://maps.google.com?q=${editLat},${editLng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                🧭 Navigare
                            </Button>
                        )}
                        {editEveniment && (() => {
                            const start = new Date(editDataStart || editEveniment.dataStart);
                            const end = new Date(editDataEnd || editEveniment.dataEnd);
                            const fmt = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                            const fmtIcs = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                            const params = new URLSearchParams({
                                action: 'TEMPLATE',
                                text: editTitlu || editEveniment.titlu,
                                dates: `${fmt(start)}/${fmt(end)}`,
                                ...(editAdresa ? { location: editAdresa } : {}),
                                details: `Vezi evenimentul în TimeTrack: ${window.location.origin}`,
                            });

                            const handleDownloadIcs = () => {
                                const titlu = (editTitlu || editEveniment.titlu).replace(/,/g, '\\,');
                                const locatie = (editAdresa || '').replace(/,/g, '\\,');
                                const ics = [
                                    'BEGIN:VCALENDAR',
                                    'VERSION:2.0',
                                    'PRODID:-//TimeTrack WebNou//RO',
                                    'BEGIN:VEVENT',
                                    `DTSTART:${fmtIcs(start)}`,
                                    `DTEND:${fmtIcs(end)}`,
                                    `SUMMARY:${titlu}`,
                                    locatie ? `LOCATION:${locatie}` : '',
                                    // Notificare 1 zi inainte
                                    'BEGIN:VALARM',
                                    'TRIGGER:-P1D',
                                    'ACTION:DISPLAY',
                                    `DESCRIPTION:Maine: ${titlu}`,
                                    'END:VALARM',
                                    // Notificare 1 ora inainte
                                    'BEGIN:VALARM',
                                    'TRIGGER:-PT1H',
                                    'ACTION:DISPLAY',
                                    `DESCRIPTION:In 1 ora: ${titlu}`,
                                    'END:VALARM',
                                    'END:VEVENT',
                                    'END:VCALENDAR',
                                ].filter(Boolean).join('\r\n');

                                const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${titlu}.ics`;
                                a.click();
                                URL.revokeObjectURL(url);
                            };

                            return (
                                <>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        href={`https://calendar.google.com/calendar/render?${params.toString()}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        📅 Google Calendar
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        onClick={handleDownloadIcs}
                                    >
                                        📥 .ics
                                    </Button>
                                </>
                            );
                        })()}
                        <Button onClick={() => setEditDialogOpen(false)}>Inchide</Button>
                        {canManage && <Button variant="contained" onClick={handleSaveEdit}>Salveaza</Button>}
                    </Box>
                </DialogActions>
            </Dialog>
        </Card>
    );
};
